import { useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import Layout from "../Layout";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { toast } from "sonner";
import { createForm } from "@formily/core";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { SchemaService } from "@/services/APIs";
import * as XLSX from "xlsx";

// Field name to display label mapping
const FIELD_LABELS: Record<string, string> = {
  caseno: "Case No",
  parcelid: "Parcel ID",
  date: "Date",
  enddate: "End Date (For Bid)",
  lienamount: "Lien Amount",
  startingbid: "Starting Bid",
  owners: "Owners",
  persqftprice: "Per Sqft Price",
  yearbuilt: "Year built",
  bed: "Bed",
  bath: "Bath",
  sqfootage: "Sq. Footage",
  address: "Address",
  propertyname: "Property name",
  finalarv: "Final ARV",
  rougharv: "Rough ARV",
  rehabestimate: "Rehab Estimate",
  notes: "Notes",
};

// Helper function to get field label
const getFieldLabel = (fieldName: string): string => {
  return FIELD_LABELS[fieldName.toLowerCase()] || fieldName;
};

// Helper function to check if a property is a layout component
const isLayoutComponent = (property: any): boolean => {
  if (!property || typeof property !== "object") {
    return false;
  }
  const isVoid = property.type === "void";
  const hasXComponent =
    property["x-component"] && typeof property["x-component"] === "string";
  const layoutComponents = [
    "Card",
    "FormTab",
    "FormLayout",
    "FormGrid",
    "Tabs",
    "FormItem",
  ];
  const componentName = property["x-component"];
  return isVoid && hasXComponent && layoutComponents.includes(componentName);
};

// Helper function to filter schema by field names
const filterSchemaByFields = (schema: any, fieldNames: string[]): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  // Handle schema with nested schema property
  if (schema.schema) {
    return {
      ...schema,
      schema: filterSchemaByFields(schema.schema, fieldNames),
    };
  }

  // If it's a property definition
  if (schema.properties) {
    const filteredProperties: any = {};

    Object.keys(schema.properties).forEach(key => {
      const property = schema.properties[key];

      // Check if this is a layout component
      if (isLayoutComponent(property)) {
        // Recursively filter layout components
        const filteredLayout = filterSchemaByFields(property, fieldNames);
        if (
          filteredLayout?.properties &&
          Object.keys(filteredLayout.properties).length > 0
        ) {
          filteredProperties[key] = filteredLayout;
        }
      } else {
        // Check if this field should be included
        const shouldInclude = fieldNames.some(
          field => field.toLowerCase() === key.toLowerCase()
        );

        if (shouldInclude) {
          filteredProperties[key] = property;
        }
      }
    });

    return {
      ...schema,
      properties: filteredProperties,
    };
  }

  // If it's an array of items
  if (schema.items) {
    return {
      ...schema,
      items: filterSchemaByFields(schema.items, fieldNames),
    };
  }

  // Recursively process other object properties
  const filtered: any = { ...schema };
  Object.keys(schema).forEach(key => {
    if (key !== "properties" && key !== "items" && key !== "schema") {
      filtered[key] = filterSchemaByFields(schema[key], fieldNames);
    }
  });

  return filtered;
};

// Field mapping: CSV/Excel column names to form field names
const FIELD_MAPPING: Record<string, string> = {
  "Case No": "caseno",
  "Case No.": "caseno",
  CaseNo: "caseno",
  "case no": "caseno",
  "Parcel ID": "parcelid",
  "Parcel ID (APN etc)": "parcelid",
  ParcelID: "parcelid",
  "parcel id": "parcelid",
  APN: "parcelid",
  Date: "date",
  date: "date",
  "End Date": "enddate",
  "End Date (For Bid)": "enddate",
  EndDate: "enddate",
  "end date": "enddate",
  "Lien Amount": "lienamount",
  LienAmount: "lienamount",
  "lien amount": "lienamount",
  "Starting Bid": "startingbid",
  StartingBid: "startingbid",
  "starting bid": "startingbid",
  "Property name": "propertyname",
  "Property Name": "propertyname",
  PropertyName: "propertyname",
  "property name": "propertyname",
  Owners: "owners",
  owners: "owners",
  "Per Sqft Price": "persqftprice",
  "Per SqftPrice": "persqftprice",
  PerSqftPrice: "persqftprice",
  "per sqft price": "persqftprice",
  "Year built": "yearbuilt",
  "Year Built": "yearbuilt",
  YearBuilt: "yearbuilt",
  "year built": "yearbuilt",
  Bed: "bed",
  bed: "bed",
  Bath: "bath",
  bath: "bath",
  "Sq. Footage": "sqfootage",
  "Sq Footage": "sqfootage",
  SqFootage: "sqfootage",
  "sq footage": "sqfootage",
  "Square Footage": "sqfootage",
  Address: "address",
  address: "address",
  "Rough ARV": "rougharv",
  RoughARV: "rougharv",
  "rough arv": "rougharv",
  "Final ARV": "finalarv",
  FinalARV: "finalarv",
  "final arv": "finalarv",
  "Rehab Estimate": "rehabestimate",
  RehabEstimate: "rehabestimate",
  "rehab estimate": "rehabestimate",
  Notes: "notes",
  notes: "notes",
  Photo: "photos",
  "Photo 1": "photos",
  Photos: "photos",
  photos: "photos",
  "Photo 2": "photos1",
  "Photos 2": "photos1",
  photos1: "photos1",
  "Photo 3": "photos2",
  "Photos 3": "photos2",
  photos2: "photos2",
  "Photo 4": "photos3",
  "Photos 4": "photos3",
  photos3: "photos3",
  "Photo 5": "photos4",
  "Photos 5": "photos4",
  photos4: "photos4",
  "Photo 6": "photos5",
  "Photos 6": "photos5",
  photos5: "photos5",
  "County Sites": "countysites",
  CountySites: "countysites",
  "county sites": "countysites",
  "County Sites 1": "countysites1",
  CountySites1: "countysites1",
  "county sites 1": "countysites1",
  "County Sites 2": "countysites2",
  CountySites2: "countysites2",
  "county sites 2": "countysites2",
};

const PP_CreatePropertyPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Define schema names
  const schemaPPPropertyMaster = "Property Master";

  // Create forms using Formily's createForm hook
  const formPPPropertyMaster = useMemo(() => createForm({}), []);
  const formPPPropertyMasterRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch schema data
  const { data: schemaData } = useQuery({
    queryKey: ["schema", schemaPPPropertyMaster],
    queryFn: () => SchemaService.getSchemaByName(schemaPPPropertyMaster),
    enabled: !!schemaPPPropertyMaster,
  });

  // Define field groups for each card
  const basicInfoFields = useMemo(
    () => [
      "caseno",
      "parcelid",
      "date",
      "enddate",
      "lienamount",
      "startingbid",
    ],
    []
  );

  const propertyDetailsFields = useMemo(
    () => [
      "propertyname",
      "owners",
      "persqftprice",
      "yearbuilt",
      "bed",
      "bath",
      "sqfootage",
      "address",
    ],
    []
  );

  const financialDetailsFields = useMemo(
    () => ["rougharv", "finalarv", "rehabestimate", "notes"],
    []
  );

  const additionalInfoFields = useMemo(
    () => [
      "photos",
      "photos1",
      "photos2",
      "photos3",
      "photos4",
      "photos5",
      "countysites",
      "countysites1",
      "countysites2",
    ],
    []
  );

  // Filter schemas for each section
  const basicInfoSchema = useMemo(() => {
    if (!schemaData) {
      return null;
    }
    const schema = schemaData.json ?? schemaData;
    return filterSchemaByFields(schema, basicInfoFields);
  }, [schemaData, basicInfoFields]);

  const propertyDetailsSchema = useMemo(() => {
    if (!schemaData) {
      return null;
    }
    const schema = schemaData.json ?? schemaData;
    return filterSchemaByFields(schema, propertyDetailsFields);
  }, [schemaData, propertyDetailsFields]);

  const financialDetailsSchema = useMemo(() => {
    if (!schemaData) {
      return null;
    }
    const schema = schemaData.json ?? schemaData;
    return filterSchemaByFields(schema, financialDetailsFields);
  }, [schemaData, financialDetailsFields]);

  const additionalInfoSchema = useMemo(() => {
    if (!schemaData) {
      return null;
    }
    const schema = schemaData.json ?? schemaData;
    return filterSchemaByFields(schema, additionalInfoFields);
  }, [schemaData, additionalInfoFields]);

  const handleAddressChange = useCallback(
    (details?: any) => {
      const formattedAddress =
        typeof details === "string"
          ? details
          : (details?.formatted_address ??
            details?.display_name ??
            details?.name ??
            "");
      formPPPropertyMaster.setValuesIn("address", formattedAddress);
    },
    [formPPPropertyMaster]
  );

  // Define required fields (excluding: notes, photo fields, county sites, rehabestimate, rougharv, propertyname)
  const requiredFields = useMemo(
    () => [
      "caseno", // Case No
      "parcelid", // Parcel ID (APN etc)
      "date", // Date
      "enddate", // End Date (For Bid)
      "lienamount", // Lien Amount
      "startingbid", // Starting Bid
      "owners", // Owners
      "persqftprice", // Per Sqft Price
      "yearbuilt", // Year built
      "bed", // Bed
      "bath", // Bath
      "sqfootage", // Sq. Footage
      "address", // Address
      // Note: finalarv is auto-calculated, so we don't require it
      // Note: propertyname is optional
    ],
    []
  );

  // Define numeric fields that should only accept numbers
  const numericFields = useMemo(
    () => [
      "caseno", // Case No
      "parcelid", // Parcel ID (APN etc)
      "lienamount", // Lien Amount
      "startingbid", // Starting Bid
      "persqftprice", // Per Sqft Price
      "yearbuilt", // Year built
      "bed", // Bed
      "bath", // Bath
      "sqfootage", // Sq. Footage
    ],
    []
  );

  // Set up required field validators and make finalarv read-only
  useEffect(() => {
    // Make finalarv field read-only/disabled since it's auto-calculated elsewhere
    formPPPropertyMaster.setFieldState("finalarv", state => {
      state.disabled = true;
      state.readOnly = true;
      state.componentProps = {
        ...state.componentProps,
        disabled: true,
        readOnly: true,
      };
    });

    // Swap the address field component to Google Places autocomplete
    formPPPropertyMaster.setFieldState("address", state => {
      state.component = GoogleAddressInput as any;
      state.componentProps = {
        ...state.componentProps,
        placeholder: "Search address or place",
        onChange: handleAddressChange,
        types: ["establishment", "geocode"], // Allow both businesses/establishments and addresses
      };
    });

    // Set required validators for all required fields
    requiredFields.forEach(fieldName => {
      formPPPropertyMaster.setFieldState(fieldName, state => {
        state.required = true;
        state.validator = (value: any) => {
          const fieldLabel = getFieldLabel(fieldName);

          // Check if field is required and empty
          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (typeof value === "string" && value.trim() === "") ||
            (typeof value === "number" && isNaN(value))
          ) {
            return `${fieldLabel} is required`;
          }

          // Check if numeric field contains only numbers
          if (numericFields.includes(fieldName)) {
            // Convert to string for validation if it's a number
            const stringValue =
              typeof value === "number" ? String(value) : String(value).trim();

            // Check if it's a valid number (allows integers and decimals)
            // Allow empty string (handled by required check above)
            if (stringValue !== "" && !/^-?\d*\.?\d+$/.test(stringValue)) {
              return `${fieldLabel} must be a valid number`;
            }

            // For integer fields (bed, bath, yearbuilt), ensure it's a whole number
            const integerFields = [
              "bed",
              "bath",
              "yearbuilt",
              "caseno",
              "parcelid",
            ];
            if (integerFields.includes(fieldName)) {
              const numValue = parseFloat(stringValue);
              if (!Number.isInteger(numValue)) {
                return `${fieldLabel} must be a whole number`;
              }
              // Ensure non-negative for counts/years
              if (numValue < 0) {
                return `${fieldLabel} must be a positive number`;
              }
            }
          }

          return undefined;
        };
      });
    });
  }, [
    formPPPropertyMaster,
    handleAddressChange,
    requiredFields,
    numericFields,
  ]);

  // Helper function to get field value from form (handles nested structures)
  const getFieldValue = (formValues: any, fieldName: string): any => {
    // Check top level first
    if (formValues[fieldName] !== undefined) {
      return formValues[fieldName];
    }
    // Check nested structures
    const nestedPaths = [
      "basicInfo",
      "propertyDetails",
      "financialDetails",
      "basicGrid",
      "propertyGrid",
      "financialGrid",
      "layoutContainer",
    ];
    for (const path of nestedPaths) {
      if (formValues[path]?.[fieldName] !== undefined) {
        return formValues[path][fieldName];
      }
    }
    return undefined;
  };

  // Handle file upload and parse CSV/Excel
  const _handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Validate file type
      const fileName = file.name.toLowerCase();
      const isValidFile =
        fileName.endsWith(".csv") ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls");

      if (!isValidFile) {
        toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      try {
        toast.loading("Parsing file...", { id: "parse-file" });

        let data: any[] = [];

        if (fileName.endsWith(".csv")) {
          // Parse CSV using XLSX library for better handling of quoted values
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            toast.error(
              "CSV file must have at least a header row and one data row",
              {
                id: "parse-file",
              }
            );
            return;
          }

          // First row is headers
          const headers = (jsonData[0] as any[]).map(h =>
            String(h ?? "").trim()
          );

          // Rest are data rows
          data = (jsonData.slice(1) as any[][]).map(row => {
            const rowData: any = {};
            headers.forEach((header, index) => {
              rowData[header] =
                row[index] !== undefined ? String(row[index] ?? "").trim() : "";
            });
            return rowData;
          });
        } else {
          // Parse Excel
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            toast.error(
              "Excel file must have at least a header row and one data row",
              {
                id: "parse-file",
              }
            );
            return;
          }

          // First row is headers
          const headers = (jsonData[0] as any[]).map(h =>
            String(h ?? "").trim()
          );

          // Rest are data rows
          data = (jsonData.slice(1) as any[][]).map(row => {
            const rowData: any = {};
            headers.forEach((header, index) => {
              rowData[header] =
                row[index] !== undefined ? String(row[index] ?? "").trim() : "";
            });
            return rowData;
          });
        }

        if (data.length === 0) {
          toast.error("No data found in file", { id: "parse-file" });
          return;
        }

        // Get first row of data
        const firstRow = data[0];

        // Map CSV/Excel columns to form fields
        const formData: any = {};

        Object.keys(firstRow).forEach(columnName => {
          const fieldName =
            FIELD_MAPPING[columnName] ?? columnName.toLowerCase();
          let value = firstRow[columnName];

          // Skip empty values
          if (value === null || value === undefined || value === "") {
            return;
          }

          // Convert value based on field type
          if (numericFields.includes(fieldName)) {
            // Try to convert to number
            const numValue = parseFloat(String(value));
            if (!isNaN(numValue)) {
              value = numValue;
            }
          }

          // Handle date fields
          if (fieldName === "date" || fieldName === "enddate") {
            // Try to parse date
            const dateValue = new Date(value);
            if (!isNaN(dateValue.getTime())) {
              value = dateValue.toISOString().split("T")[0];
            }
          }

          // Handle photo and county sites arrays (if multiple values separated by comma)
          if (
            fieldName.startsWith("photos") ||
            fieldName.startsWith("countysites")
          ) {
            if (typeof value === "string" && value.includes(",")) {
              value = value
                .split(",")
                .map((v: string) => v.trim())
                .filter((v: string) => v);
            } else if (value) {
              value = [String(value)];
            }
          }

          formData[fieldName] = value;
        });

        // Set form values
        Object.keys(formData).forEach(fieldName => {
          formPPPropertyMaster.setFieldState(fieldName, state => {
            state.value = formData[fieldName];
          });
        });

        // Handle address separately if it exists
        if (formData.address) {
          handleAddressChange(formData.address);
        }

        toast.success(
          `Successfully loaded data from ${file.name}. Please review and save.`,
          { id: "parse-file" }
        );

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error: any) {
        console.error("Error parsing file:", error);
        toast.error(
          error?.message ?? "Error parsing file. Please check the file format.",
          { id: "parse-file" }
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [formPPPropertyMaster, handleAddressChange, numericFields]
  );

  // Handle button click to trigger file input
  const _handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // First, validate the form - this will throw if validation fails
      await formPPPropertyMaster.validate();

      // Additional check: ensure required fields have values
      const formValues = formPPPropertyMaster.values;
      const missingFields: string[] = [];

      requiredFields.forEach(fieldName => {
        const value = getFieldValue(formValues, fieldName);
        // Check if field is empty
        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          (typeof value === "string" && value.trim() === "") ||
          (typeof value === "number" && isNaN(value));

        if (isEmpty) {
          missingFields.push(getFieldLabel(fieldName));
        }
      });

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in all required fields: ${missingFields.join(", ")}`
        );
        return;
      }

      // Trigger form save using handleSave
      await formPPPropertyMasterRef.current?.handleSave();
      toast.success("Property saved successfully!");

      // Invalidate and refetch all related queries using predicate for more reliable matching
      await queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey;
          return (
            (Array.isArray(key) &&
              key[0] === "moduleData" &&
              key[1] === schemaPPPropertyMaster) ||
            (Array.isArray(key) &&
              key[0] === "columnGridData" &&
              key[1] === schemaPPPropertyMaster)
          );
        },
      });

      // Also refetch queries to ensure data is loaded
      await queryClient.refetchQueries({
        predicate: query => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === "moduleData" &&
            key[1] === schemaPPPropertyMaster
          );
        },
      });

      navigate("/property-portal/property-master");
    } catch (error: any) {
      // Extract error message
      let errorMessage = "Error saving property!";

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data) {
        const data = error.response.data;
        errorMessage =
          data?.error?.message ??
          data?.message ??
          data?.error ??
          data?.non_field_errors?.[0] ??
          "Error saving property!";

        // Format field-specific errors if present
        if (data && typeof data === "object" && !data.message && !data.error) {
          const fieldErrors: string[] = [];
          Object.keys(data).forEach(key => {
            if (Array.isArray(data[key])) {
              fieldErrors.push(`${key}: ${data[key].join(", ")}`);
            } else if (typeof data[key] === "string") {
              fieldErrors.push(`${key}: ${data[key]}`);
            }
          });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join("; ");
          }
        }
      } else if (error?.errors) {
        errorMessage = "Please fill in all required fields";
      }

      toast.error(errorMessage);
      console.error("Form validation/save error:", error);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl space-y-4 lg:space-y-6">
        <div>
          <h1 className="w-1/2 text-2xl font-bold text-foreground lg:text-3xl">
            Property Information
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Hidden SchemaFormAutoSubmit for form submission */}
          <div style={{ display: "none" }}>
            <SchemaFormAutoSubmit
              form={formPPPropertyMaster}
              schemaName={schemaPPPropertyMaster}
              recordId={id}
              ref={formPPPropertyMasterRef}
            />
          </div>

          {/* Styling for form fields - matching exact shadcn/ui Input styling */}
          <style>{`
            .property-form-styled .ant-formily-item {
              margin-bottom: 0;
              display: flex;
              flex-direction: column;
            }
            .property-form-styled .ant-formily-item-label {
              padding-bottom: 0.5rem;
            }
            .property-form-styled .ant-formily-item-label > label {
              font-size: 0.875rem;
              font-weight: 500;
              line-height: 1.25rem;
              color: hsl(var(--foreground));
              margin-bottom: 0;
            }
            .property-form-styled .ant-input-affix-wrapper,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined,
            .property-form-styled .ant-input,
            .property-form-styled .ant-picker input {
              display: flex !important;
              height: 2.5rem !important;
              width: 100% !important;
              border-radius: calc(var(--radius) - 2px) !important;
              border-width: 1px !important;
              border-style: solid !important;
              border-color: hsl(var(--input)) !important;
              background-color: hsl(var(--background)) !important;
              padding-top: 0.5rem !important;
              padding-bottom: 0.5rem !important;
              padding-left: 0.75rem !important;
              padding-right: 0.75rem !important;
              font-size: .875rem !important;
              line-height: 1.5rem !important;
              transition: all 0.2s !important;
              --tw-ring-offset-color: hsl(var(--background));
              box-shadow: none !important;
            }
            .property-form-styled .ant-input-number {
              display: flex !important;
              height: 2.5rem !important;
              width: 100% !important;
              border-radius: calc(var(--radius) - 2px) !important;
              border-width: 1px !important;
              border-style: solid !important;
              border-color: hsl(var(--input)) !important;
              background-color: hsl(var(--background)) !important;
              padding-top: 0.5rem !important;
              padding-bottom: 0.5rem !important;
              padding-left: 0.75rem !important;
              padding-right: 0.75rem !important;
              font-size: 0.875rem !important;
              line-height: 1.25rem !important;
              transition: all 0.2s !important;
              --tw-ring-offset-color: hsl(var(--background));
              box-shadow: none !important;
            }
            .property-form-styled .ant-input-number .ant-input-number-input {
              border: none !important;
              padding: 0 !important;
              height: auto !important;
              background: transparent !important;
              box-shadow: none !important;
              flex: 1 !important;
              width: auto !important;
              display: block !important;
            }
            .property-form-styled .ant-input-number .ant-input-number-handler-wrap {
              display: none !important;
            }
            .property-form-styled .ant-input-affix-wrapper > input.ant-input,
            .property-form-styled .ant-input-affix-wrapper > textarea.ant-input,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined > input.ant-input,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined > textarea.ant-input {
              border: none !important;
              border-radius: 0 !important;
              padding: 0 !important;
              height: auto !important;
              background: transparent !important;
              box-shadow: none !important;
              outline: none !important;
              font-size: inherit !important;
              color: inherit !important;
              width: 100% !important;
            }
            @media (min-width: 768px) {
              .property-form-styled .ant-input-affix-wrapper,
              .property-form-styled .ant-input-affix-wrapper.ant-input-outlined,
              .property-form-styled .ant-input,
              .property-form-styled .ant-picker input {
                font-size: 0.875rem !important;
                line-height: 1.25rem !important;
              }
            }
            .property-form-styled .ant-input-number:focus-within,
            .property-form-styled .ant-input-number-focused {
              outline: none !important;
              box-shadow: 0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring)) !important;
              border-color: hsl(var(--ring)) !important;
            }
            .property-form-styled .ant-input-number .ant-input-number-input::placeholder {
              color: hsl(var(--muted-foreground)) !important;
            }
            .property-form-styled .ant-input-number.ant-input-number-disabled {
              cursor: not-allowed !important;
              opacity: 0.5 !important;
            }
            .property-form-styled .ant-picker,
            .property-form-styled .ant-picker.ant-picker-outlined {
              width: 100% !important;
              height: 2.5rem !important;
              border-radius: calc(var(--radius) - 2px) !important;
              border-width: 1px !important;
              border-style: solid !important;
              border-color: hsl(var(--input)) !important;
              background-color: hsl(var(--background)) !important;
              padding-top: 0.5rem !important;
              padding-bottom: 0.5rem !important;
              padding-left: 0.75rem !important;
              padding-right: 0.75rem !important;
              box-shadow: none !important;
            }
            .property-form-styled .ant-picker input,
            .property-form-styled .ant-picker.ant-picker-outlined input,
            .property-form-styled .ant-picker .ant-picker-input > input {
              border: none !important;
              border-radius: 0 !important;
              padding: 0 !important;
              height: auto !important;
              background: transparent !important;
              box-shadow: none !important;
              outline: none !important;
              font-size: inherit !important;
              color: inherit !important;
              width: 100% !important;
              position: relative !important;
              display: inline-block !important;
              line-height: inherit !important;
              transition: inherit !important;
              flex: auto !important;
              min-width: 1px !important;
              font-family: inherit !important;
            }
            .property-form-styled .ant-input-affix-wrapper:focus-within,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined:focus-within,
            .property-form-styled .ant-input:focus-visible,
            .property-form-styled .ant-picker:focus-within,
            .property-form-styled .ant-picker.ant-picker-outlined:focus-within,
            .property-form-styled .ant-picker:focus-visible input,
            .property-form-styled .ant-input-focused,
            .property-form-styled .ant-picker-focused,
            .property-form-styled .ant-picker-focused input,
            .property-form-styled .ant-input-number-focused {
              outline: none !important;
              box-shadow: 0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring)) !important;
              border-color: hsl(var(--ring)) !important;
            }
            .property-form-styled .ant-input-affix-wrapper .ant-input::placeholder,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined .ant-input::placeholder,
            .property-form-styled .ant-input::placeholder,
            .property-form-styled .ant-picker input::placeholder {
              color: hsl(var(--muted-foreground)) !important;
            }
            .property-form-styled .ant-input-affix-wrapper.ant-input-disabled,
            .property-form-styled .ant-input-affix-wrapper.ant-input-outlined.ant-input-disabled,
            .property-form-styled .ant-input:disabled,
            .property-form-styled .ant-picker-disabled,
            .property-form-styled .ant-picker.ant-picker-outlined.ant-picker-disabled {
              cursor: not-allowed !important;
              opacity: 0.5 !important;
            }
            .property-form-styled .ant-textarea {
              display: flex;
              min-height: 80px;
              width: 100%;
              border-radius: calc(var(--radius) - 2px);
              border-width: 1px;
              border-style: solid;
              border-color: hsl(var(--input));
              background-color: hsl(var(--background));
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
              padding-left: 0.75rem;
              padding-right: 0.75rem;
              font-size: 0.875rem;
              line-height: 1.25rem;
              --tw-ring-offset-color: hsl(var(--background));
            }
            .property-form-styled .ant-textarea:focus-visible {
              outline: none;
              box-shadow: 0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring));
            }
            .property-form-styled .ant-textarea:disabled {
              cursor: not-allowed;
              opacity: 0.5;
            }
            .property-form-styled .ant-textarea::placeholder {
              color: hsl(var(--muted-foreground));
            }
            .property-form-styled .ant-formily-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            }
            @media (max-width: 768px) {
              .property-form-styled .ant-formily-grid {
                grid-template-columns: 1fr;
              }
            }
            .property-form-styled .ant-formily-item-control {
              width: 100%;
            }
            .property-form-styled .ant-formily-item-control-input {
              width: 100%;
            }
            .property-form-styled .ant-formily-item-control-input-content {
              width: 100%;
            }
            .property-form-styled .space-y-2 > :not([hidden]) ~ :not([hidden]) {
              --tw-space-y-reverse: 0;
              margin-top: calc(0.5rem * calc(1 - var(--tw-space-y-reverse)));
              margin-bottom: calc(0.5rem * var(--tw-space-y-reverse));
            }


            @media (max-width: 1024px) {
.property-form-styled  .ant-formily-grid-layout {
grid-template-columns: repeat(2, minmax(0px, 1fr)) !important;

}
          `}</style>

          {/* Basic Information */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="property-form-styled">
                {basicInfoSchema && (
                  <SchemaFormRender
                    form={formPPPropertyMaster}
                    schemaJson={basicInfoSchema}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Property Details */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="property-form-styled">
                {propertyDetailsSchema && (
                  <SchemaFormRender
                    form={formPPPropertyMaster}
                    schemaJson={propertyDetailsSchema}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial Details */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Financial Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <style>{`
                .financial-grid .ant-formily-grid {
                  grid-template-columns: repeat(3, 1fr);
                }
                @media (max-width: 768px) {
                  .financial-grid .ant-formily-grid {
                    grid-template-columns: 1fr;
                  }
                }
              `}</style>
              <div className="property-form-styled financial-grid">
                {financialDetailsSchema && (
                  <SchemaFormRender
                    form={formPPPropertyMaster}
                    schemaJson={financialDetailsSchema}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <style>{`
                .additional-grid .ant-formily-grid {
                  grid-template-columns: repeat(5, 1fr);
                }
                @media (max-width: 768px) {
                  .additional-grid .ant-formily-grid {
                    grid-template-columns: 1fr;
                  }
                }
              `}</style>
              <div className="property-form-styled additional-grid">
                {additionalInfoSchema && (
                  <SchemaFormRender
                    form={formPPPropertyMaster}
                    schemaJson={additionalInfoSchema}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
            >
              {isEditMode ? "Update Property" : "Save Property"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/property-portal/property-master")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default PP_CreatePropertyPage;
